import { IconHelp, Tooltip } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, ReactNode, memo } from 'react';
import styles from './CardTable.module.scss';
import { useIsClamped } from '@/hooks/useIsClamped';

const cx = classnames.bind(styles);

export type Props = {
  label: ReactNode;
  value: ReactNode;
  align?: 'left' | 'right';
  unit?: ReactNode;
  caption?: ReactNode;
  help?: ReactNode;
};

export const Cell: FC<Props> = memo(
  ({ label, value, align = 'left', unit, caption, help }) => {
    const { ref, isClamped } = useIsClamped<HTMLDivElement>(value);
    const { ref: labelRef, isClamped: isLabelClamped } =
      useIsClamped<HTMLSpanElement>(label);

    const valueBlock = (
      <div className={cx('value-block')}>
        <Tooltip title={isClamped ? value : undefined}>
          <div
            ref={ref}
            className={cx('value', { withCaption: Boolean(caption) })}
          >
            {value}
          </div>
        </Tooltip>
        {caption && <span className={cx('caption')}>{caption}</span>}
      </div>
    );

    return (
      <div className={cx('cell')}>
        <dt className={cx('label')}>
          <Tooltip title={isLabelClamped ? label : undefined}>
            <span ref={labelRef} className={cx('label-text')}>
              {label}
            </span>
          </Tooltip>
          {help && (
            <Tooltip title={help}>
              <span className={cx('label-help')}>
                <IconHelp />
              </span>
            </Tooltip>
          )}
        </dt>
        <dd className={cx('content', { 'align-right': align === 'right' })}>
          {valueBlock}
          {unit && <span className={cx('unit')}>{unit}</span>}
        </dd>
      </div>
    );
  }
);
Cell.displayName = 'Cell';

/**
 * Empty cell padding out the last row. Renders borders only.
 */
export const FillerCell: FC = memo(() => (
  <div className={cx('cell')} aria-hidden='true' />
));
FillerCell.displayName = 'FillerCell';
